import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "reactstrap";
import LanguageSwitch from "./LanguageSwitch";
import SubscriptionStep from "./SubscriptionStep";
import { GetLandingPageConfigs } from "landing/actions/SubscriptionActionCreators";
import type { LandingPageConfig } from "landing/objects/LandingPageConfig";
import {
  ChangeGoogleTagID,
  ChangeLanguageCode,
} from "main/actions/IAppActionCreators";
import type {
  IMainAppState,
  ThunkDispatchType,
} from "main/reducers/IMainAppState";
import {
  GetOAuthPath,
  type OAuthType,
} from "oauth/actions/OAuthActionCreators";
import Loader from "shared/loader/Loader";

export interface ISubscriptionProps {
  loadedLanguage: string;
  onChangeLandingPageConfig: (landingPageConfig: LandingPageConfig) => void;
}

export default ({
  loadedLanguage,
  onChangeLandingPageConfig,
}: ISubscriptionProps) => {
  const error =
    "This link is invalid or expired. Please text in again. Ce lien n'est pas valide ou a expiré. S'il vous plaît essayez à nouveau.\n";
  const dispatch: ThunkDispatchType = useDispatch();

  const [loadLandingPagePromise, setLandingPagePromise] = useState<
    Promise<LandingPageConfig[]> | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  // Get Language Code
  const languageCode = useSelector(
    (state: IMainAppState) => state.app.languageCode,
  );

  // Get Query Params.
  const queryParams: { [key: string]: string } = useMemo(() => {
    const queryString: string[] = window.location.search
      .substring(1)
      .split("&");
    return queryString.reduce((params, paramString) => {
      const pair: string[] = paramString.split("=");
      params[pair[0]] = pair[1];
      return params;
    }, {});
  }, [window.location.search]);

  const setError = (errorMessage: string) => {
    setErrorMessage(errorMessage);
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 3000);
  };

  // Load Error Message.
  useEffect(() => {
    const errorMsg: string | undefined = queryParams.errorMsg;
    if (errorMsg && errorMsg === "oauth_error") {
      setError(errorMsg);
    }
  }, [queryParams.errorMsg]);

  // Load Landing Page Configs.
  useEffect(() => {
    setLandingPagePromise(
      (async () => {
        if (window.location.hostname.split(".").length === 2) {
          console.error("Subdomain is not found");
          throw error;
        }

        const lpCode: string | undefined = queryParams.lpCode;

        if (!lpCode || lpCode.length < 6) {
          console.error("lp code is not found");
          throw error;
        }

        let landingPageConfigs;
        try {
          landingPageConfigs = await dispatch(GetLandingPageConfigs(lpCode));
        } catch (ex) {
          console.error("API Error ", ex);
          throw error;
        }

        // Change Language Code.
        dispatch(
          ChangeLanguageCode(
            landingPageConfigs[0].storeLanguageCode,
            landingPageConfigs[0].offerLpTemplateId,
          ),
        );
        onChangeLandingPageConfig(landingPageConfigs[0]);

        // Update GTM.
        dispatch(ChangeGoogleTagID(landingPageConfigs[0].googleTagId));

        return landingPageConfigs;
      })(),
    );
  }, [dispatch, queryParams.lpCode, onChangeLandingPageConfig]);

  const handleGoOAuth = (oAuthType: OAuthType, lpCode: string): void => {
    dispatch(GetOAuthPath(oAuthType, lpCode))
      .then((url) => {
        window.location.href = url;
      })
      .catch(() => {
        setError("oauth_error");
      });
  };

  return (
    <Loader promise={loadLandingPagePromise}>
      {(landingPageConfigs: LandingPageConfig[]) => {
        const landingPageConfig =
          landingPageConfigs.find(
            (landingPageConfig) =>
              landingPageConfig.languageCode === languageCode,
          ) || landingPageConfigs[0];

        // If the relevant language files are not loaded, ignore.
        if (
          loadedLanguage !==
          languageCode + landingPageConfig.offerLpTemplateId
        ) {
          return null;
        }

        const languages = landingPageConfigs.map(
          (config) => config.languageCode,
        );

        return (
          <>
            {landingPageConfig.stylesheet && (
              <style
                dangerouslySetInnerHTML={{
                  __html: landingPageConfig.stylesheet,
                }}
              />
            )}

            {errorMessage && (
              <Alert color="danger">
                <span className="fas fa-exclamation-circle" />{" "}
                <FormattedMessage
                  id={`landing.subscription.social.${errorMessage}`}
                />
              </Alert>
            )}

            <header>
              <LanguageSwitch
                languages={languages}
                languageCode={languageCode}
              />
              <h1>
                <img src={landingPageConfig.logoUrl} />
              </h1>
            </header>

            <SubscriptionStep
              landingPageConfig={landingPageConfig}
              languageCode={languageCode}
              onGoOAuth={handleGoOAuth}
            />
          </>
        );
      }}
    </Loader>
  );
};
